import {
  hasOnlySingleSpaces,
  LIMITS,
  PASSWORD_PADDED_MESSAGE,
  PASSWORD_SPACING_MESSAGE,
  PASSWORD_TOO_LONG_MESSAGE,
  PASSWORD_TOO_SHORT_MESSAGE,
} from "../../../shared/contracts.js";

/**
 * The rules a password must meet before it is set, judged in place with the
 * words the server's `passwordSchema` / `temporaryPasswordSchema` answer, so
 * a field error reads the same whether the browser or the server raised it.
 * Shared by the change-password page (the person's own new password) and
 * the account dialogs (a temporary password the administrator types).
 */

export const PASSWORD_MIN_LENGTH = LIMITS.password.min;
export const PASSWORD_MAX_LENGTH = LIMITS.password.max;

/** The length rule alone: 12–128 characters, never trimmed first. */
export function passwordLengthError(password: string): string | undefined {
  if (password.length < PASSWORD_MIN_LENGTH) return PASSWORD_TOO_SHORT_MESSAGE;
  if (password.length > PASSWORD_MAX_LENGTH) return PASSWORD_TOO_LONG_MESSAGE;
  return undefined;
}

/**
 * The optional temporary password field: blank means "generate one" and is
 * never an error; anything typed must meet the length rule and, since a
 * password is never trimmed, must not begin or end with whitespace — the
 * person will type exactly what they were handed — nor hold any inner
 * whitespace but single spaces, which the HTML email would fold or disguise
 * (`temporaryPasswordSchema`'s rule, in the same order).
 */
export function temporaryPasswordError(password: string): string | undefined {
  if (password.length === 0) return undefined;
  const length = passwordLengthError(password);
  if (length) return length;
  if (password !== password.trim()) return PASSWORD_PADDED_MESSAGE;
  if (!hasOnlySingleSpaces(password)) return PASSWORD_SPACING_MESSAGE;
  return undefined;
}
